/**
 * ============================================================================
 * INTEGRATION ACTIVITY SERVICE - Google Workspace
 * ============================================================================
 * Activity tracking for Gmail, Calendar, and Google Tasks integrations
 *
 * Features:
 * - Gmail activity logging (received, sent, labeled, etc.)
 * - Calendar activity logging (created, updated, meeting links, etc.)
 * - Google Tasks activity logging (created, completed, synced, etc.)
 * - Integration with webhook delivery system
 * - Audit trail for all Google Workspace operations
 *
 * @pattern Phase 4 - IService + ServiceResponse + typedSupabase
 * @version 1.0.0
 * @date 2026-02-20
 */
import { typedSupabase } from "../lib/typed-supabase";
import type { IService, ServiceResponse } from "./service-registry";
import { createServiceError} from "./service-registry";
import { logger } from "../lib/logger";
import { enqueueWebhookDelivery } from "./mycelium-webhook-enqueue";
import { publishIntegrationActivity } from "./mycelium-integration-publish";
// ============================================================================
// TYPES
// ============================================================================
export interface GmailActivity {
    id: string;
    user_id: string;
    organization_id: string | null;
    message_id: string;
    action_type: string;
    event_type: string;
    from_address: string | null;
    to_addresses: string[] | null;
    cc_addresses: string[] | null;
    subject: string | null;
    labels: string[] | null;
    thread_id: string | null;
    details: Record<string, unknown>;
    created_at: string;
}
export interface CalendarActivity {
    id: string;
    user_id: string;
    organization_id: string | null;
    event_id: string;
    action_type: string;
    event_type: string;
    event_title: string | null;
    event_description: string | null;
    start_time: string | null;
    end_time: string | null;
    location: string | null;
    meeting_link: string | null;
    attendees: string[] | null;
    calendar_id: string | null;
    details: Record<string, unknown>;
    created_at: string;
}
export interface GTasksActivity {
    id: string;
    user_id: string;
    organization_id: string | null;
    task_id: string;
    action_type: string;
    event_type: string;
    task_title: string | null;
    task_notes: string | null;
    due_date: string | null;
    completed_date: string | null;
    list_id: string | null;
    list_name: string | null;
    parent_task_id: string | null;
    sync_direction: string | null;
    conflict_resolution: string | null;
    details: Record<string, unknown>;
    created_at: string;
}
// Activity logging parameters
export interface LogGmailActivityParams {
    userId: string;
    organizationId?: string;
    messageId: string;
    actionType: string; // 'received', 'sent', 'replied', 'forwarded', 'labeled', 'archived', 'deleted', 'sync_completed'
    eventType: string; // 'gmail.message_received', etc.
    fromAddress?: string;
    toAddresses?: string[];
    ccAddresses?: string[];
    subject?: string;
    labels?: string[];
    threadId?: string;
    details?: Record<string, unknown>;
    triggerWebhooks?: boolean; // Default true
}
export interface LogCalendarActivityParams {
    userId: string;
    organizationId?: string;
    eventId: string;
    actionType: string; // 'created', 'updated', 'deleted', 'started', 'ended', 'meeting_link_created', 'attendee_responded'
    eventType: string; // 'calendar.event_created', etc.
    eventTitle?: string;
    eventDescription?: string;
    startTime?: string;
    endTime?: string;
    location?: string;
    meetingLink?: string;
    attendees?: string[];
    calendarId?: string;
    details?: Record<string, unknown>;
    triggerWebhooks?: boolean; // Default true
}
export interface LogGTasksActivityParams {
    userId: string;
    organizationId?: string;
    taskId: string;
    actionType: string; // 'created', 'updated', 'completed', 'deleted', 'sync_completed', 'conflict_detected'
    eventType: string; // 'gtasks.task_created', etc.
    taskTitle?: string;
    taskNotes?: string;
    dueDate?: string;
    completedDate?: string;
    listId?: string;
    listName?: string;
    parentTaskId?: string;
    syncDirection?: string; // 'to_google', 'from_google', 'bidirectional'
    conflictResolution?: string; // 'google_wins', 'flowstate_wins', 'manual'
    details?: Record<string, unknown>;
    triggerWebhooks?: boolean; // Default true
}
// ============================================================================
// SERVICE INTERFACE
// ============================================================================
export interface IIntegrationActivityService extends IService {
    // Gmail activity methods
    logGmailActivity(params: LogGmailActivityParams): Promise<ServiceResponse<string>>;
    getGmailActivities(userId?: string, organizationId?: string, limit?: number, offset?: number): Promise<ServiceResponse<GmailActivity[]>>;
    // Calendar activity methods
    logCalendarActivity(params: LogCalendarActivityParams): Promise<ServiceResponse<string>>;
    getCalendarActivities(userId?: string, organizationId?: string, limit?: number, offset?: number): Promise<ServiceResponse<CalendarActivity[]>>;
    // Google Tasks activity methods
    logGTasksActivity(params: LogGTasksActivityParams): Promise<ServiceResponse<string>>;
    getGTasksActivities(userId?: string, organizationId?: string, limit?: number, offset?: number): Promise<ServiceResponse<GTasksActivity[]>>;
}
// ============================================================================
// SERVICE IMPLEMENTATION
// ============================================================================
export class IntegrationActivityService implements IIntegrationActivityService {
    readonly name = "IntegrationActivityService";
    async initialize(): Promise<void> {
        logger.info("✅ IntegrationActivityService initialized");
    }
    async dispose(): Promise<void> {
        logger.info("🧹 IntegrationActivityService disposed");
    }
    // ==========================================================================
    // GMAIL ACTIVITY METHODS
    // ==========================================================================
    async logGmailActivity(params: LogGmailActivityParams): Promise<ServiceResponse<string>> {
        const startTime = Date.now();
        logger.debug("Logging Gmail activity", { eventType: params.eventType, messageId: params.messageId });
        try {
            const { data: activityId, error } = await typedSupabase.rpc("log_gmail_activity", {
                p_user_id: params.userId,
                p_organization_id: params.organizationId || null,
                p_message_id: params.messageId,
                p_action_type: params.actionType,
                p_event_type: params.eventType,
                p_from_address: params.fromAddress || null,
                p_to_addresses: params.toAddresses || null,
                p_cc_addresses: params.ccAddresses || null,
                p_subject: params.subject || null,
                p_labels: params.labels || null,
                p_thread_id: params.threadId || null,
                p_details: params.details ? JSON.parse(JSON.stringify(params.details)) : {},
            });
            if (error) {
                logger.error("Error logging Gmail activity", { error, params });
                return { success: false, error: error.message, code: "LOG_FAILED", service: this.name };
            }
            // Trigger webhooks if enabled and organization exists
            if (params.triggerWebhooks !== false && params.organizationId) {
                this.deliverWebhook(activityId as string, params.organizationId, "gmail", params.eventType, {
                    message_id: params.messageId,
                    action_type: params.actionType,
                    from_address: params.fromAddress,
                    to_addresses: params.toAddresses,
                    subject: params.subject,
                    labels: params.labels,
                    thread_id: params.threadId,
                    ...params.details,
                }).catch((error) => {
                    logger.warn("Failed to deliver webhook for Gmail activity", { error, activityId });
                });
                publishIntegrationActivity({
                    userId: params.userId,
                    organizationId: params.organizationId,
                    activityId: activityId as string,
                    activityDomain: "gmail",
                    eventType: params.eventType,
                    payload: {
                        messageId: params.messageId,
                        message_id: params.messageId,
                        action_type: params.actionType,
                        subject: params.subject,
                        from_address: params.fromAddress,
                        to_addresses: params.toAddresses,
                        thread_id: params.threadId,
                    },
                }).catch((error) => {
                    logger.warn("Failed to publish Gmail activity to Mycelium", { error, activityId });
                });
            }
            logger.debug("Gmail activity logged successfully", {
                activityId,
                duration: Date.now() - startTime
            });
            return { success: true, data: activityId as string };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            logger.error("Unexpected error logging Gmail activity", { error: serviceError, params });
            return { success: false, error: serviceError.message, code: "LOG_FAILED", service: this.name };
        }
    }
    async getGmailActivities(userId?: string, organizationId?: string, limit: number = 100, offset: number = 0): Promise<ServiceResponse<GmailActivity[]>> {
        logger.debug("Fetching Gmail activities", { userId, organizationId, limit, offset });
        try {
            const { data, error } = await typedSupabase.rpc("get_gmail_activities", {
                p_user_id: userId || null,
                p_organization_id: organizationId || null,
                p_limit: limit,
                p_offset: offset,
            });
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name };
            }
            return { success: true, data: (data as GmailActivity[]) || [] };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name };
        }
    }
    // ==========================================================================
    // CALENDAR ACTIVITY METHODS
    // ==========================================================================
    async logCalendarActivity(params: LogCalendarActivityParams): Promise<ServiceResponse<string>> {
        const startTime = Date.now();
        logger.debug("Logging Calendar activity", { eventType: params.eventType, eventId: params.eventId });
        try {
            const { data: activityId, error } = await typedSupabase.rpc("log_calendar_activity", {
                p_user_id: params.userId,
                p_organization_id: params.organizationId || null,
                p_event_id: params.eventId,
                p_action_type: params.actionType,
                p_event_type: params.eventType,
                p_event_title: params.eventTitle || null,
                p_event_description: params.eventDescription || null,
                p_start_time: params.startTime || null,
                p_end_time: params.endTime || null,
                p_location: params.location || null,
                p_meeting_link: params.meetingLink || null,
                p_attendees: params.attendees || null,
                p_calendar_id: params.calendarId || null,
                p_details: params.details ? JSON.parse(JSON.stringify(params.details)) : {},
            });
            if (error) {
                logger.error("Error logging Calendar activity", { error, params });
                return { success: false, error: error.message, code: "LOG_FAILED", service: this.name };
            }
            // Trigger webhooks if enabled and organization exists
            if (params.triggerWebhooks !== false && params.organizationId) {
                this.deliverWebhook(activityId as string, params.organizationId, "calendar", params.eventType, {
                    event_id: params.eventId,
                    action_type: params.actionType,
                    event_title: params.eventTitle,
                    start_time: params.startTime,
                    end_time: params.endTime,
                    location: params.location,
                    meeting_link: params.meetingLink,
                    attendees: params.attendees,
                    ...params.details,
                }).catch((error) => {
                    logger.warn("Failed to deliver webhook for Calendar activity", { error, activityId });
                });
                publishIntegrationActivity({
                    userId: params.userId,
                    organizationId: params.organizationId,
                    activityId: activityId as string,
                    activityDomain: "calendar",
                    eventType: params.eventType,
                    payload: {
                        eventId: params.eventId,
                        event_id: params.eventId,
                        title: params.eventTitle,
                        event_title: params.eventTitle,
                        startTime: params.startTime,
                        end_time: params.endTime,
                    },
                }).catch((error) => {
                    logger.warn("Failed to publish Calendar activity to Mycelium", { error, activityId });
                });
            }
            logger.debug("Calendar activity logged successfully", {
                activityId,
                duration: Date.now() - startTime
            });
            return { success: true, data: activityId as string };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            logger.error("Unexpected error logging Calendar activity", { error: serviceError, params });
            return { success: false, error: serviceError.message, code: "LOG_FAILED", service: this.name };
        }
    }
    async getCalendarActivities(userId?: string, organizationId?: string, limit: number = 100, offset: number = 0): Promise<ServiceResponse<CalendarActivity[]>> {
        logger.debug("Fetching Calendar activities", { userId, organizationId, limit, offset });
        try {
            const { data, error } = await typedSupabase.rpc("get_calendar_activities", {
                p_user_id: userId || null,
                p_organization_id: organizationId || null,
                p_limit: limit,
                p_offset: offset,
            });
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name };
            }
            return { success: true, data: (data as CalendarActivity[]) || [] };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name };
        }
    }
    // ==========================================================================
    // GOOGLE TASKS ACTIVITY METHODS
    // ==========================================================================
    async logGTasksActivity(params: LogGTasksActivityParams): Promise<ServiceResponse<string>> {
        const startTime = Date.now();
        logger.debug("Logging Google Tasks activity", { eventType: params.eventType, taskId: params.taskId });
        try {
            const { data: activityId, error } = await typedSupabase.rpc("log_gtasks_activity", {
                p_user_id: params.userId,
                p_organization_id: params.organizationId || null,
                p_task_id: params.taskId,
                p_action_type: params.actionType,
                p_event_type: params.eventType,
                p_task_title: params.taskTitle || null,
                p_task_notes: params.taskNotes || null,
                p_due_date: params.dueDate || null,
                p_completed_date: params.completedDate || null,
                p_list_id: params.listId || null,
                p_list_name: params.listName || null,
                p_parent_task_id: params.parentTaskId || null,
                p_sync_direction: params.syncDirection || null,
                p_conflict_resolution: params.conflictResolution || null,
                p_details: params.details ? JSON.parse(JSON.stringify(params.details)) : {},
            });
            if (error) {
                logger.error("Error logging Google Tasks activity", { error, params });
                return { success: false, error: error.message, code: "LOG_FAILED", service: this.name };
            }
            // Trigger webhooks if enabled and organization exists
            if (params.triggerWebhooks !== false && params.organizationId) {
                this.deliverWebhook(activityId as string, params.organizationId, "gtasks", params.eventType, {
                    task_id: params.taskId,
                    action_type: params.actionType,
                    task_title: params.taskTitle,
                    due_date: params.dueDate,
                    completed_date: params.completedDate,
                    list_name: params.listName,
                    sync_direction: params.syncDirection,
                    conflict_resolution: params.conflictResolution,
                    ...params.details,
                }).catch((error) => {
                    logger.warn("Failed to deliver webhook for Google Tasks activity", { error, activityId });
                });
                publishIntegrationActivity({
                    userId: params.userId,
                    organizationId: params.organizationId,
                    activityId: activityId as string,
                    activityDomain: "gtasks",
                    eventType: params.eventType,
                    payload: {
                        taskId: params.taskId,
                        task_id: params.taskId,
                        task_title: params.taskTitle,
                        due_date: params.dueDate,
                    },
                }).catch((error) => {
                    logger.warn("Failed to publish GTasks activity to Mycelium", { error, activityId });
                });
            }
            logger.debug("Google Tasks activity logged successfully", {
                activityId,
                duration: Date.now() - startTime
            });
            return { success: true, data: activityId as string };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            logger.error("Unexpected error logging Google Tasks activity", { error: serviceError, params });
            return { success: false, error: serviceError.message, code: "LOG_FAILED", service: this.name };
        }
    }
    async getGTasksActivities(userId?: string, organizationId?: string, limit: number = 100, offset: number = 0): Promise<ServiceResponse<GTasksActivity[]>> {
        logger.debug("Fetching Google Tasks activities", { userId, organizationId, limit, offset });
        try {
            const { data, error } = await typedSupabase.rpc("get_gtasks_activities", {
                p_user_id: userId || null,
                p_organization_id: organizationId || null,
                p_limit: limit,
                p_offset: offset,
            });
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name };
            }
            return { success: true, data: (data as GTasksActivity[]) || [] };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name };
        }
    }
    // ==========================================================================
    // PRIVATE: WEBHOOK DELIVERY
    // ==========================================================================
    private async deliverWebhook(activityId: string, organizationId: string, activityDomain: string, eventType: string, payload: Record<string, unknown>): Promise<void> {
        try {
            await enqueueWebhookDelivery({
                sourceDomain: activityDomain,
                sourceType: eventType,
                sourceEventId: activityId,
                orgId: organizationId,
                eventPayload: payload,
                originalTimestamp: new Date().toISOString(),
                source: "IntegrationActivityService",
            });
        }
        catch (error) {
            logger.error("Error enqueueing webhook delivery", { error, activityId, eventType });
            // Don't throw - webhook delivery is fire-and-forget
        }
    }
}
// ============================================================================
// SINGLETON INSTANCE
// ============================================================================
export const integrationActivityService = new IntegrationActivityService();
